<template>
    <div
        v-if="type === 'multi-select' && showSelectAllOption && shouldShow"
        class="d-flex justify-content-start align-items-start flex-wrap"
        style="gap: 1rem"
    >
        <!-- Multi-select with "Select All" option -->
        <Field
            v-if="!isAllSites"
            :type="type"
            :label="label"
            :disabled="disabled"
            :key="siteOptions.length"
            v-model="selectedSite"
            :required="required"
            :plain-text-mode="plainTextMode"
            :params="{ options: siteOptions, placeholder: placeholder }"
            class="flex-grow-1"
        />
        <Field
            v-else
            :label="label"
            :disabled="true"
            :required="required"
            v-model="allSitesInputText"
            class="flex-grow-1"
        />
        <Field
            class="pt-4"
            label="Select All Sites"
            type="checkbox"
            v-model="isAllSites"
            @click="toggleAllSites"
            :disabled="disabled"
        />
    </div>
    <Field
        v-else-if="shouldShow"
        :type="type"
        :label="label"
        :key="siteOptions.length"
        :disabled="disabled"
        v-model="selectedSite"
        :required="required"
        :plain-text-mode="plainTextMode"
        :params="{ options: siteOptions, placeholder: placeholder }"
    />
</template>

<script>
import Field from '@/components/Field.vue';

export default {
    name: 'SiteDropdownField',
    components: {
        Field,
    },
    props: {
        modelValue: {
            type: [String, Array],
            default: null,
        },
        label: {
            type: String,
            default: '',
        },
        type: {
            type: String,
            default: 'select',
        },
        required: {
            type: Boolean,
            default: false,
        },
        showSelectAllOption: {
            type: Boolean,
            default: false,
        },
        placeholder: {
            type: String,
            default: 'Select a site',
        },
        plainTextMode: {
            type: Boolean,
            default: false,
        },
        disabled: {
            type: Boolean,
            default: false,
        },
        hideWhenGlobalActive: {
            type: Boolean,
            default: false,
        },
    },
    emits: ['update:modelValue'],
    data() {
        return {
            isAllSites: false,
            preSelectedSites: [],
        };
    },
    computed: {
        siteOptions() {
            return this.$siteStore.getSiteOptions || [];
        },
        allSitesInputText() {
            return `All ${this.siteOptions.length} sites`;
        },
        selectedSite: {
            get() {
                return this.modelValue;
            },
            set(value) {
                this.$emit('update:modelValue', value);
            },
        },
        // Check if global site is active
        isGlobalSiteActive() {
            const globalId = this.$siteStore.globalSiteId;
            return globalId !== null && globalId !== undefined;
        },
        shouldShow() {
            if (this.hideWhenGlobalActive && this.isGlobalSiteActive) {
                return false;
            }
            return true;
        },
    },
    watch: {
        modelValue: {
            immediate: true,
            handler(val) {
                if (!this.showSelectAllOption) return;
                this.isAllSites = this.hasAllOptions(val, this.siteOptions);
            },
        },
        siteOptions: {
            immediate: true,
            handler(opts) {
                if (!this.showSelectAllOption) return;
                this.isAllSites = this.hasAllOptions(this.modelValue, opts);
            },
        },
    },
    methods: {
        toggleAllSites(isAllSites) {
            if (isAllSites) {
                // Save current selection before selecting all
                this.preSelectedSites = this.modelValue || [];
                // Select all site values
                const allSiteValues = this.siteOptions.map(
                    (site) => site.value
                );
                this.$emit('update:modelValue', allSiteValues);
            } else {
                // Restore previous selection
                this.$emit('update:modelValue', this.preSelectedSites);
            }
        },

        // Checks if the selected options are all the options
        hasAllOptions(val, opts) {
            if (!Array.isArray(val) || !opts || opts.length === 0) return false;
            const selected = new Set(val);
            return opts.every((o) => selected.has(o.value));
        },
    },
};
</script>

<style scoped></style>
